// R99 — "NOTHING MATCHES THAT YET. TRY ANOTHER WORD." OVER A DIRECTORY THAT NEVER ARRIVED.
//
// The Angels drawer holds TWO reads and **one of them was already honest**, which is W12/R26/R35/
// R38/R65's shape yet again: the capability lives on the surface that needed it first. `run()` — the
// gated match, the one that spends — reads its status, tells a 401 apart from a refusal, honours the
// server's own sentence and falls back to the room's. The BROWSE beside it, five lines up, was:
//
//     fetch(`/api/angels?${params}`)
//       .then((r) => r.json())
//       .then((data) => { … setEntries(Array.isArray(data?.entries) ? data.entries : []); })
//       .catch(() => { if (!cancelled) setEntries([]); })
//
// **NO STATUS READ, AND TWO SEPARATE FORGERIES OF AN EMPTY DIRECTORY** — one in the unwrap and one
// in the `.catch` — under a panel that then prints
//
//     "Nothing matches that yet. Try another word."
//
// **A SENTENCE ABOUT HER SEARCH, DRAWN OVER A REFERENCE THAT NEVER LOADED**: R60's Card Library
// finding and R64's Cauldron finding word for word, and R70's Spellbook one book over. It is the
// wrong instruction as well as a wrong sentence — there is no other word that will work.
//
// **AND IT IS NOT HYPOTHETICAL ON THIS HOST.** `handleAngelsList` is synchronous over a committed
// catalog and cannot refuse, so the reachable failures are the transport ones — and ITEM 13b exists
// because this site is redeployed under a live crowd. A drawer opened across a restart is exactly
// the case: 502 from the edge, an HTML body, and a member told the 72 angels are not in there.
//
// **THE ANSWER IS `T[] | null` AND DELIBERATELY NOT THE FOUR-WAY ONE** (R58's ruling). This route
// sits in front of committed data, refuses nobody and composes no sentence, so a `sign-in` or a
// `refused` arm here would be a second answer key for answers it cannot give. `null` is "we could
// not read it" and an empty array is "the directory has nothing under that word" (R18, R39's third
// state).
//
// `doFetch` is a PARAMETER (R46). This module imports NOTHING (the entry requirement for a
// `scripts/`-loadable law).

export const angelDirectoryPath = "/api/angels";

// The room speaks English top to bottom, so its sentences are declared here rather than as catalog
// rows — R60's ruling on the Ask Lala and Card Library surfaces, where a lone translated line over
// an untranslated screen would be the defect (14-B6).
export const ANGEL_DIRECTORY_COPY = {
  // Two sentences, and only ONE of them is a claim about what is in the directory.
  unread: "The directory could not be read just now. Try that once more.",
  empty: "Nothing matches that yet. Try another word.",
};

export function angelDirectoryUrl(category: string, q: string) {
  const params = new URLSearchParams({ category });
  if (q.trim()) params.set("q", q.trim());
  return `${angelDirectoryPath}?${params.toString()}`;
}

export type AngelDirectoryAnswer<E, C> = {
  // `null` on anything that did not land. Never `[]`, which is the answer the catalog gives when it
  // really has nothing under that word.
  entries: E[] | null;
  categories: C[] | null;
};

export async function readAngelDirectory<E, C>(
  category: string,
  q: string,
  doFetch: typeof fetch = typeof fetch === "function" ? fetch : (undefined as never),
): Promise<AngelDirectoryAnswer<E, C>> {
  const nothing: AngelDirectoryAnswer<E, C> = { entries: null, categories: null };
  let response: Response;
  try {
    response = await doFetch(angelDirectoryUrl(category, q));
  } catch {
    return nothing;
  }
  if (!response.ok) { await response.body?.cancel?.().catch(() => {}); return nothing; }
  const data = await response.json().catch(() => null) as { entries?: unknown; categories?: unknown } | null;
  // PRESENCE, never truthiness (R53): a 200 carrying no `entries` ARRAY did not answer this question,
  // whatever else it carried.
  if (!data || !Array.isArray(data.entries)) return nothing;
  return {
    entries: data.entries as E[],
    // The categories are the panel's CHIPS and they do not change with the search, so a read that
    // brought none leaves the ones already on screen standing (`null`, not `[]`). That was the one
    // half of this read that was already right, and it is kept.
    categories: Array.isArray(data.categories) && data.categories.length ? (data.categories as C[]) : null,
  };
}

// WHAT THE PANEL SAYS UNDER THE LIST. A read that landed with rows says NOTHING — the directory
// appearing IS the answer. The two other states are two different sentences, and the order is the
// whole point: an unread directory is not an empty one.
export function angelDirectoryNote<E, C>(
  answer: AngelDirectoryAnswer<E, C>,
  copy: { unread: string; empty: string } = ANGEL_DIRECTORY_COPY,
): string {
  if (answer.entries === null) return copy.unread;
  if (!answer.entries.length) return copy.empty;
  return "";
}
